// Opt-in collaborative sync adapter (spec §4.8). The host application owns
// ConekUi::FlowChannel; this adapter speaks a documented op protocol over the
// subscription. Server → client: { ops: [{ op, ...payload }] }.
// Client → server: local engine mutations forwarded as perform("mutate", op).
import { createConsumer } from "@rails/actioncable"
import { Controller } from "@hotwired/stimulus"

// Op types this adapter knows how to apply (ROW 10) — anything else in a
// received batch is dropped with a warning instead of reaching the engine.
const KNOWN_OPS = new Set(["addNode", "removeNode", "moveNode", "addEdge", "removeEdge", "updateEdge", "updateNode"])

export default class extends Controller {
  static values = { url: String, flowId: { type: String, default: "" } }
  static outlets = ["ui--flow"]

  connect() {
    this._applying = false
    if (!this.urlValue) return
    this._consumer = createConsumer(this.urlValue)
    this._subscription = this._consumer.subscriptions.create(
      { channel: "ConekUi::FlowChannel", flow_id: this.flowIdValue },
      { received: (msg) => this._apply(msg) }
    )
    this._bindBroadcast()
  }

  disconnect() {
    if (this._consumer) { this._consumer.disconnect(); this._consumer = null; this._subscription = null }
    this._unbindBroadcast()
  }

  // Server → local: apply ops against the flow outlet. The _applying guard
  // keeps these remote ops from echoing back out as broadcasts.
  //
  // ROW 10: each op applies in ISOLATION — a malformed op is shape-checked
  // and dropped with a warning (index included), a throwing apply is caught,
  // and neither case stops the rest of the batch or escapes the handler (one
  // bad op used to crash `received` and silently drop every remaining op).
  _apply(msg) {
    if (!msg || !Array.isArray(msg.ops)) return
    const flow = this.hasUiFlowOutlet ? this.uiFlowOutlet : null
    if (!flow) return
    this._applying = true
    try {
      msg.ops.forEach((op, index) => {
        try {
          const problem = this._opProblem(op)
          if (problem) { console.warn(`[ui--flow-sync] dropping op #${index}: ${problem}`, op); return }
          switch (op.op) {
            case "addNode":    flow.addNode?.(op.node); break
            // The op's node IS the full fresh def — replaceNode converges the
            // local entry to EXACTLY it (keys the sender dropped lose their
            // map entries and DOM projections), where a key-by-key updateNode
            // merge would keep stale keys. The op itself is unchanged: still
            // named updateNode, still the full def (updateEdge → replaceEdge
            // parity). The _applying guard above covers this case too, so the
            // peer's own nodechange emission is suppressed (no echo loop).
            case "updateNode": flow.replaceNode?.(op.node); break
            case "removeNode": flow.removeNode?.(op.id); break
            case "moveNode": {
              // ROW 10b: peers forward x/y as-is — a malformed value
              // ("10rem", null, "abc") would silently poison the stored node
              // position and its serialized toObject()/save output. Coerce
              // numeric strings ("42" → 42); reject everything non-finite.
              const x = this._coerceFinite(op.x), y = this._coerceFinite(op.y)
              if (x === null || y === null) {
                console.warn(`[ui--flow-sync] dropping op #${index}: non-finite moveNode coordinates (x: ${String(op.x)}, y: ${String(op.y)})`, op)
                return
              }
              flow.moveNode(op.id, x, y)
              break
            }
            case "addEdge":    flow.addEdge?.(op.edge); break
            case "removeEdge": flow.removeEdge?.(op.id); break
            // The op's edge IS the full fresh def — replaceEdge converges the
            // local entry to EXACTLY it (keys the sender dropped lose their
            // map entries and DOM projections), where a key-by-key updateEdge
            // merge would keep stale keys. The op itself is unchanged: still
            // named updateEdge, still the full def. The _applying guard above
            // covers this case too, so the peer's own edgechange emission is
            // suppressed (no echo loop).
            case "updateEdge": flow.replaceEdge?.(op.edge); break
          }
        } catch (err) {
          console.warn(`[ui--flow-sync] op #${index} (${op && op.op}) failed to apply — skipped`, err)
        }
      })
    } finally {
      this._applying = false
    }
  }

  // Minimal shape check BEFORE dispatch: the op is an object with a known
  // type, and id-carrying ops carry a non-empty string id (a missing/garbage
  // id keys the engine's maps on "undefined" — the F6 ghost, now gated for
  // every op type, addNode included). Returns a human-readable problem, or
  // null when the op is dispatchable.
  _opProblem(op) {
    if (!op || typeof op !== "object") return "op is not an object"
    if (typeof op.op !== "string" || !KNOWN_OPS.has(op.op)) return `unknown op "${op.op}"`
    switch (op.op) {
      case "removeNode":
      case "moveNode":
      case "removeEdge":
        if (typeof op.id !== "string" || op.id === "") return "id must be a non-empty string"
        break
      case "addNode":
      case "updateNode":
        // updateNode shares the addNode gate: the op's node IS the full def
        // peers converge through (like updateEdge below), so a def with no
        // id cannot key the engine's map — drop it here.
        if (!op.node || typeof op.node !== "object" || typeof op.node.id !== "string" || op.node.id === "") {
          return "node must be an object with a non-empty string id"
        }
        break
      case "addEdge":
      case "updateEdge":
        // F6 (kept): addEdge requires the id-carrying edge def — an op
        // without an id (a legacy peer forwarding the raw connect payload)
        // would key the map entry "undefined" and render a ghost. updateEdge
        // shares the gate: a def with no id cannot key the engine's map.
        if (!op.edge || typeof op.edge !== "object" || typeof op.edge.id !== "string" || op.edge.id === "") {
          return "edge must be an object with a non-empty string id"
        }
        break
    }
    return null
  }

  // Number coercion for remote coordinates: finite numbers pass through,
  // non-empty numeric strings coerce, everything else (null, "", "abc",
  // "10rem", NaN, Infinity, objects) is rejected as null.
  _coerceFinite(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null
    if (typeof value === "string" && value.trim() !== "") {
      const n = Number(value)
      return Number.isFinite(n) ? n : null
    }
    return null
  }

  // Local → server: forward engine mutation events as ops. Local ops applied
  // from _apply MUST NOT re-broadcast (guard flag while applying).
  _bindBroadcast() {
    const el = this.hasUiFlowOutlet ? this.uiFlowOutlet.element : null
    if (!el) return
    this._broadcastEl = el
    this._onNodeMove = (e) => this._broadcast({ op: "moveNode", id: e.detail.nodeId, x: e.detail.x, y: e.detail.y })
    this._onNodeDelete = (e) => this._broadcast({ op: "removeNode", id: e.detail.nodeId })
    this._onEdgeDelete = (e) => this._broadcast({ op: "removeEdge", id: e.detail.edgeId })
    // F6: broadcast from edgesadd, NOT connect. `connect` carries the raw
    // {sourceNode, sourceHandle, targetNode, targetHandle} gesture payload —
    // forwarded as an addEdge op it reached peers with no id/source/target and
    // rendered a broken `data-edge="undefined"` ghost. edgesadd carries the
    // CREATED edge def (id included) — exactly what remote addEdge expects.
    // Multi-edge emissions (paste, restore) fan out to one op per edge; the
    // _applying guard still suppresses echoes of remote-applied ops.
    this._onEdgesAdd = (e) => {
      const edges = Array.isArray(e.detail?.edges) ? e.detail.edges : []
      edges.forEach(edge => this._broadcast({ op: "addEdge", edge }))
    }
    // Mirror of the F6 edgesadd fix: broadcast the CREATED node def (id and
    // back-filled handles included — the plain JSON map entry, never the DOM
    // element), one addNode op per node. Unlike connect/edgesadd there is no
    // second creation event for nodes: addNode, paste (one multi-node
    // emission), duplicate, drop and menu/keyboard adds each emit nodesadd
    // exactly once per created node, so forwarding cannot ghost-duplicate.
    // Restore paths (undo/redo/fromObject) emit nodesadd per restored-new
    // node and edgesadd per restored-new edge — disjoint sets, each entity
    // forwarded exactly once. Forwarding restore-sourced nodes too is
    // deliberate: skipping them while edgesadd forwards restore-sourced edges
    // would leave peers holding edges that point at nodes they never
    // received. The _applying guard still suppresses echoes of
    // remote-applied ops.
    this._onNodesAdd = (e) => {
      const nodes = Array.isArray(e.detail?.nodes) ? e.detail.nodes : []
      nodes.forEach(node => this._broadcast({ op: "addNode", node }))
    }
    // updateEdge (backlog): forward the FULL fresh def so peers converge on
    // every carried key — a host-driven rewire and the undo/redo restore of
    // a reconnect (B1 rides updateEdge) both reach peers this way. The
    // edgechange detail keeps its long-pinned {edgeId, attrs} shape, so the
    // def is read back from the engine (the event fires after the map
    // write); the _applying guard in _broadcast suppresses the echo of a
    // remote-applied op, same as the other five.
    this._onEdgeChange = (e) => {
      const edge = e.detail?.edgeId ? this.uiFlowOutlet?.getEdge?.(e.detail.edgeId) : null
      if (edge) this._broadcast({ op: "updateEdge", edge })
    }
    // updateNode (node-side mirror of updateEdge above): forward the FULL
    // fresh def so peers converge on every carried key — host updateNode
    // patches, setNodeHidden/toggleNodeCollapsed and the restore branch's
    // converged replaces all emit nodechange. The nodechange detail keeps
    // its {nodeId, attrs} shape, so the def is read back from the engine
    // (the event fires after the map write); the plain JSON map entry, never
    // the DOM element. The _applying guard in _broadcast suppresses the echo
    // of a remote-applied op, same as the other six.
    this._onNodeChange = (e) => {
      const node = e.detail?.nodeId ? this.uiFlowOutlet?.getNode?.(e.detail.nodeId) : null
      if (node) this._broadcast({ op: "updateNode", node })
    }
    el.addEventListener("ui--flow:nodemove", this._onNodeMove)
    el.addEventListener("ui--flow:nodedelete", this._onNodeDelete)
    el.addEventListener("ui--flow:edgedelete", this._onEdgeDelete)
    el.addEventListener("ui--flow:edgesadd", this._onEdgesAdd)
    el.addEventListener("ui--flow:nodesadd", this._onNodesAdd)
    el.addEventListener("ui--flow:edgechange", this._onEdgeChange)
    el.addEventListener("ui--flow:nodechange", this._onNodeChange)
  }

  _unbindBroadcast() {
    const el = this._broadcastEl
    if (!el) return
    el.removeEventListener("ui--flow:nodemove", this._onNodeMove)
    el.removeEventListener("ui--flow:nodedelete", this._onNodeDelete)
    el.removeEventListener("ui--flow:edgedelete", this._onEdgeDelete)
    el.removeEventListener("ui--flow:edgesadd", this._onEdgesAdd)
    el.removeEventListener("ui--flow:nodesadd", this._onNodesAdd)
    el.removeEventListener("ui--flow:edgechange", this._onEdgeChange)
    el.removeEventListener("ui--flow:nodechange", this._onNodeChange)
    this._broadcastEl = null
  }

  _broadcast(op) {
    if (this._applying) return
    this._subscription?.perform("mutate", { op })
  }
}
